import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LikeOutlined, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import type { HomeLatestViewpoint } from "../../SpaceType";
import { avatarDisplayWord } from "../../../utils/avatarDisplayWord";
import { h5NavigateTo } from "../../../utils/miniProgramEnv";
import { resolveAppAvatarUrl } from "../../../utils/resolveAppMediaUrl";

function formatEngagementCount(n: number): string {
  if (n >= 10000) {
    return `${(n / 10000).toFixed(1)}万`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n);
}

function displayPublishedCompact(publishTime: string): string {
  const t = publishTime.trim();
  if (!t) {
    return "";
  }
  const d = dayjs(t);
  return d.isValid() ? d.format("M月D日 HH:mm") : t;
}

function lockedViewpointHint(viewpoint: HomeLatestViewpoint): string {
  const name = viewpoint.requiredProductDisplayName?.trim();
  if (name) {
    return `订阅「${name}」后可查看全文`;
  }
  return "订阅观点产品后可查看全文";
}

function nicknameOf(viewpoint: HomeLatestViewpoint): string {
  const nick = viewpoint.publisherNickname?.trim();
  if (nick) {
    return nick;
  }
  const label = viewpoint.publisherLabel?.trim();
  if (label) {
    return label;
  }
  return "用户";
}

function resolveImageUrls(viewpoint: HomeLatestViewpoint): string[] {
  const urls = viewpoint.imageUrls?.map((u) => u.trim()).filter(Boolean) ?? [];
  if (urls.length > 0) {
    return urls.slice(0, 9);
  }
  const single = viewpoint.imageUrl?.trim();
  return single ? [single] : [];
}

/** 最新观点 */
export function SpaceHomeViewpointPreview(props: {
  spaceId: string;
  viewpoint: HomeLatestViewpoint;
  fallbackSpaceProductId?: string | null;
}) {
  const { spaceId, viewpoint, fallbackSpaceProductId } = props;
  const navigate = useNavigate();
  const accessible = viewpoint.contentAccessible;
  const nick = nicknameOf(viewpoint);
  const importance = viewpoint.importance?.trim() || "normal";
  const showImportance = importance === "important" || importance === "urgent";
  const titleText =
    viewpoint.titleMode && viewpoint.title?.trim() ? viewpoint.title.trim() : null;
  const categoryName = viewpoint.categoryName?.trim() || "";
  const imageUrls = resolveImageUrls(viewpoint);
  const likeCount = viewpoint.likeCount ?? 0;
  const collectCount = viewpoint.collectCount ?? 0;
  const targetSpaceProductId =
    viewpoint.spaceProductId?.trim() || fallbackSpaceProductId?.trim() || "";
  const clickable = Boolean(targetSpaceProductId);

  const onViewMore = useCallback(() => {
    if (!targetSpaceProductId) {
      return;
    }
    h5NavigateTo(navigate, `/space/${spaceId}/${encodeURIComponent(targetSpaceProductId)}`);
  }, [navigate, spaceId, targetSpaceProductId]);

  const importanceLabel =
    importance === "urgent" ? "精选" : importance === "important" ? "重要" : "";

  const imageGridClass =
    imageUrls.length === 1
      ? "space-home-moment-images space-home-moment-images--single"
      : "space-home-moment-images";

  return (
    <section className="space-home-feed__section" aria-label="最新观点">
      <div className="space-home-feed__section-head">
        <p className="space-home-feed__section-hint">最新观点</p>
        {targetSpaceProductId ? (
          <button type="button" className="space-home-feed__section-more" onClick={onViewMore}>
            查看更多
          </button>
        ) : null}
      </div>
      <article
        className={
          accessible
            ? "space-home-moment"
            : "space-home-moment space-home-moment--locked"
        }
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? onViewMore : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onViewMore();
                }
              }
            : undefined
        }
      >
        <div className="space-home-moment-head">
          <EasyAvatar
            shape="circle"
            size={32}
            url={resolveAppAvatarUrl(viewpoint.publisherAvatarUrl) || undefined}
            word={avatarDisplayWord(nick, viewpoint.publisherLabel)}
            alt=""
            className="space-home-moment-avatar"
          />
          <div className="space-home-moment-meta">
            <div className="space-home-moment-meta-left">
              <span className="space-home-moment-nick">{nick}</span>
              <span className="space-home-moment-time">
                {displayPublishedCompact(viewpoint.publishTime)}
              </span>
            </div>
            <div className="space-home-moment-actions" aria-label="点赞与收藏">
              <span>
                <LikeOutlined aria-hidden />
                {formatEngagementCount(likeCount)}
              </span>
              <span>
                <StarOutlined aria-hidden />
                {formatEngagementCount(collectCount)}
              </span>
            </div>
          </div>
        </div>
        {titleText ? <div className="space-home-moment-title">{titleText}</div> : null}
        <div className="space-home-moment-body">
          {showImportance ? (
            <span
              className={
                importance === "urgent"
                  ? "space-home-moment-imp space-home-moment-imp--urgent"
                  : "space-home-moment-imp space-home-moment-imp--important"
              }
            >
              {importanceLabel}
            </span>
          ) : null}
          {accessible ? (
            <>
              {viewpoint.content}
              {categoryName ? (
                <span className="space-home-moment-tag">#{categoryName}</span>
              ) : null}
            </>
          ) : (
            <>
              <span className="space-home-moment-preview">{viewpoint.content}</span>
              <span className="space-home-moment-lock-hint">{lockedViewpointHint(viewpoint)}</span>
            </>
          )}
        </div>
        {accessible && imageUrls.length > 0 ? (
          <div className={imageGridClass}>
            {imageUrls.map((u, idx) => (
              <div key={`${idx}-${u}`} className="space-home-moment-image-cell">
                <img src={u} alt="" loading="lazy" />
              </div>
            ))}
          </div>
        ) : null}
      </article>
    </section>
  );
}
